import { useQuery } from '@tanstack/react-query';
import { Bot, CalendarDays } from 'lucide-react';
import { AGENT_LINK_LABEL, useDayAgentLink } from '../../components/DayCardAgentLink';
import { DayPasses } from '../../components/DayPasses';
import { EngineDayTrace, EngineNotCalculated, EngineReceipt } from '../../components/EngineDay';
import { Button, SidePanel, Skeleton } from '../../components/ui';
import { api, rulesKeys } from '../../lib/api';
import { ApiError, reportClientError } from '../../lib/errors';
import { ruDate, weekday } from '../../lib/format';
import { useRole } from '../../lib/role';
import type { OpenDay } from './ExampleDay';
import { shouldReport } from './LoadError';
import { LABEL, T, say } from './texts';

/**
 * «Открыть расчёт дня» со страницы правил (004, T046; research R6; contracts/ui.md, «Карточка правила»): правая
 * панель (тот же `SidePanel`, что у карточки дня 003 — поверх листа у правого края, до 620 px, до 900 px — на весь
 * экран) с карточкой дня только для чтения по `GET /api/rules/day-explain`: человек и дата, «Проходы за день»,
 * «Как посчитано», подробности расчёта. Без правки, ответов и отметок; в подвале — «Закрыть».
 * «Передать день агенту» (005, T138, FR-064) — второе действие подвала: здесь роли строки «Выборки дней» (администратор
 * правил, утверждающий, проверяющий) видят реальный день. Видно, если сервер ответил `canShareDay` в «Моих выборках» и
 * день посчитан (передавать нечего, пока расчёта нет); окно со ссылкой — `useDayAgentLink`, как в карточке дня.
 */
export function DayPanel({
  day,
  onClose,
  tzOffset,
}: {
  /** Открытый день; `null` — панель закрыта. */
  day: OpenDay | null;
  onClose: () => void;
  tzOffset: string;
}) {
  const { role } = useRole();
  const q = useQuery({
    queryKey: rulesKeys.dayExplain(role, day?.personId ?? '', day?.date ?? ''),
    queryFn: async () => {
      if (!day) throw new Error('день не выбран');
      try {
        return await api.rulesDayExplain(day.personId, day.date);
      } catch (e) {
        if (shouldReport(e)) reportClientError('Rules', 'load day explain', e);
        throw e;
      }
    },
    enabled: day !== null,
  });
  const engine = q.data?.calculated ? q.data : null;
  const errorId = q.error instanceof ApiError ? q.error.requestId : null;
  const dayText = day ? `${weekday(day.date)}, ${ruDate(day.date)}` : '';
  const agentLink = useDayAgentLink({
    personId: day?.personId ?? '',
    date: day?.date ?? '',
    open: day !== null,
    tzOffset,
    subtitle: day ? `${day.name} · ${dayText}` : '',
    screen: 'Rules',
  });
  return (
    <SidePanel
      open={day !== null}
      onClose={onClose}
      label={LABEL.dayPanel}
      title={LABEL.dayPanel}
      icon={CalendarDays}
      footer={
        <>
          {agentLink.allowed && engine && (
            <Button icon={Bot} onClick={agentLink.start}>
              {AGENT_LINK_LABEL.menu}
            </Button>
          )}
          <Button className="ml-auto" onClick={onClose}>
            {LABEL.close}
          </Button>
        </>
      }
    >
      {day && (
        <>
          <div>
            <h2 className="text-[19px] leading-[1.3] font-semibold tracking-[-0.015em] break-words text-ink">
              {day.name}
            </h2>
            <p className="mt-0.5 text-[13px] text-ink-2">{dayText}</p>
          </div>
          {q.isLoading && (
            <div aria-busy="true" aria-label={LABEL.loading} className="space-y-3">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          )}
          {q.error && (
            <p role="alert" className="text-[13px] text-bad">
              {T.dayError}
              {errorId && <span className="block text-[12px] text-ink-3">{say.errorCode(errorId)}</span>}
            </p>
          )}
          {q.data && !q.data.calculated && <EngineNotCalculated />}
          {engine && (
            <>
              <DayPasses day={engine.day} date={day.date} />
              <section aria-label={LABEL.howCounted} className="text-[13px]">
                <h3 className="mb-2 text-[14px] font-semibold text-ink">{LABEL.howCounted}</h3>
                <EngineReceipt engine={engine} tzOffset={tzOffset} date={day.date} />
              </section>
              <EngineDayTrace engine={engine} tzOffset={tzOffset} />
            </>
          )}
        </>
      )}
      {/* Окно «Передать день агенту» — модальное (`<dialog>` поверх всего), закрывается вместе с панелью. */}
      {agentLink.dialog}
    </SidePanel>
  );
}
